/**
 * UI Components - Moon Moon Agency  
 * Handles dropdowns, read more functionality, and loader states
 */

/*********************************************************************************************
 * 🚀 LOADER MODULE
 */
const LoaderManager = {
	// Local configuration
	config: {
		classes: {
			loading: 'mm-loading',
			active: 'mm-active',
			expanded: 'mm-expanded'
		},
		selectors: {
			dropdownTrigger: '[data-dropdown-trigger]',
			readMoreToggle: '[data-read-more-toggle]',
			readMoreContainer: '[data-read-more]',
			readMoreText: '[data-read-more-text]',
			readMoreLabelShow: '[data-read-more-show]',
			readMoreLabelHide: '[data-read-more-hide]'
		}
	},

	/**
	 * Add loading state to specified element
	 */
	loadThis(elem) {
		if (!elem) {
			window.log('warn', 'LoaderManager', 'No element provided');
			return;
		}
		elem.classList.add(this.config.classes.loading);
		window.emit('global:loader-activated', { element: elem.tagName });
	}
};

/*********************************************************************************************
 * 📋 DROPDOWN MODULE
 */
const DropdownManager = {
	/**
	 * Close all dropdowns
	 */
	closeAll(dropWrappers) {
		dropWrappers.forEach((el) => {
			el.classList.remove(LoaderManager.config.classes.active);
			el.setAttribute('aria-expanded', 'false');
			if (el.nextElementSibling) {
				el.nextElementSibling.style.maxHeight = null;
			}
		});
	},

	/**
	 * Toggle dropdown
	 */
	toggle(trigger, dropWrappers) {
		// Close other dropdowns
		dropWrappers.forEach((el) => {
			if (el !== trigger) {
				el.classList.remove(LoaderManager.config.classes.active);
				el.setAttribute('aria-expanded', 'false');
				if (el.nextElementSibling) {
					el.nextElementSibling.style.maxHeight = null;
				}
			}
		});

		// Toggle current dropdown
		trigger.classList.toggle(LoaderManager.config.classes.active);
		const content = trigger.nextElementSibling;

		if (content) {
			const isActive = trigger.classList.contains(LoaderManager.config.classes.active);
			trigger.setAttribute('aria-expanded', isActive.toString());
			content.style.maxHeight = isActive ? `${content.scrollHeight}px` : null;

			window.emit('global:dropdown-toggled', {
				active: isActive,
				element: trigger.tagName
			});
		}
	},

	/**
	 * Initialize dropdown behaviors
	 */
	init() {
		const dropWrappers = window.getElements(LoaderManager.config.selectors.dropdownTrigger);
		if (!dropWrappers.length) return;

		// Initialize aria-expanded on all dropdowns
		dropWrappers.forEach((item) => {
			if (!item.hasAttribute('aria-expanded')) {
				item.setAttribute('aria-expanded', 'false');
			}

			// Click event
			item.addEventListener('click', () => {
				this.toggle(item, dropWrappers);
			});

			// Keyboard support (Enter/Space)
			item.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this.toggle(item, dropWrappers);
				}
			});

			// Focus event (open on tab)
			item.addEventListener('focus', () => {
				// Only trigger if focused via keyboard (focus-visible) and not already active
				if (item.matches(':focus-visible') && !item.classList.contains(LoaderManager.config.classes.active)) {
					this.toggle(item, dropWrappers);
				}
			});
		});

		// Global Escape key handler
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' || e.key === 'Esc') {
				const hasActiveDropdown = Array.from(dropWrappers).some((el) =>
					el.classList.contains(LoaderManager.config.classes.active)
				);

				if (hasActiveDropdown) {
					this.closeAll(dropWrappers);
					// Return focus to active dropdown trigger
					const activeTrigger = Array.from(dropWrappers).find((el) =>
						el.classList.contains(LoaderManager.config.classes.active)
					);
					activeTrigger?.focus();
				}
			}
		});
	}
};

/*********************************************************************************************
 * 📖 READ MORE MODULE
 */
const ReadMoreManager = {
	/**
	 * Toggle read more state
	 */
	toggle(btn) {
		const container = btn.closest(LoaderManager.config.selectors.readMoreContainer);
		if (!container) return;

		const textElement = container.querySelector(LoaderManager.config.selectors.readMoreText);
		const labelShow = btn.querySelector(LoaderManager.config.selectors.readMoreLabelShow);
		const labelHide = btn.querySelector(LoaderManager.config.selectors.readMoreLabelHide);

		if (!textElement || !labelShow || !labelHide) return;

		const expanded = btn.getAttribute('aria-expanded') === 'true';
		const newState = !expanded;

		// Update state
		textElement.classList.toggle(LoaderManager.config.classes.expanded, newState);
		btn.setAttribute('aria-expanded', newState.toString());
		labelShow.hidden = newState;
		labelHide.hidden = !newState;

		window.emit('global:read-more-toggled', { expanded: newState });
	},

	/**
	 * Initialize read more functionality
	 */
	init() {
		const toggleButtons = window.getElements(LoaderManager.config.selectors.readMoreToggle);
		if (!toggleButtons.length) return;

		toggleButtons.forEach((btn) => {
			// Initialize aria-expanded if not present
			if (!btn.hasAttribute('aria-expanded')) {
				btn.setAttribute('aria-expanded', 'false');
			}

			// Click event
			btn.addEventListener('click', () => {
				this.toggle(btn);
			});

			// Keyboard support (Enter/Space)
			btn.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this.toggle(btn);
				}
			});
		});
	}
};

/*********************************************************************************************
 * 🔗 EXTERNAL LINKS MODULE
 */
const ExternalLinksManager = {
	/**
	 * Process external links to open in new tab
	 */
	init() {
		const links = window.getElements('a:not(.consent-wrapper)');
		let processedCount = 0;

		links.forEach((link) => {
			if (link.hostname !== window.location.hostname) {
				link.target = '_blank';
				link.rel = 'noreferrer noopener';
				processedCount++;
			}
		});

	}
};

/*********************************************************************************************
 * 📏 DOCUMENT HEIGHT MODULE
 */
const DocumentHeightManager = {
	/**
	 * Update document height CSS variable
	 */
	update() {
		const newHeight = `${window.innerHeight}px`;
		document.documentElement.style.setProperty('--doc-height', newHeight);
		window.emit('global:document-height-updated', { height: newHeight });
	}
};

/*********************************************************************************************
 * 🎠 SPLIDE MODULE
 */
const SplideManager = {
	/**
	 * Initialise un seul élément Splide (destruction + création).
	 * @param {Element} element - Élément .splide
	 * @param {Array} sectionsWithSplide - Tableau à remplir avec les noms de sections
	 * @returns {number} 1 si initialisé, 0 sinon
	 */
	_initOne(element, sectionsWithSplide = []) {
		if (!window.Splide) return 0;

		const isProductMediaSlider = element.hasAttribute('data-main-slider');
		if (isProductMediaSlider) return 0;

		// In MM-Select-Cards on mobile the per-card image slider is set to
		// pointer-events: none (see mm-product-card-image-slider.liquid).
		// Splide is initialized for nothing — 8+ idle instances per page
		// that still attach touch listeners and reserve compositing layers,
		// which jank the swipe on any other slider on the same page.
		const isCardSlider = element.classList.contains('mm-card-product-image-slider');
		if (isCardSlider && element.closest('.mm-select-cards')
				&& window.matchMedia('(max-width: 768px)').matches) {
			return 0;
		}

		// Personnalisateur : éviter paginations dupliquées (destroy ne nettoie pas toujours le DOM)
		element.querySelectorAll('.splide__pagination').forEach((el) => el.remove());

		try {
			const configData = element.dataset.splide;
			let splideInstance;
			let config = null;

			if (configData) {
				const cleanedData = configData
					.replace(/'/g, '"')
					.replace(/(\w+):/g, '"$1":');
				config = JSON.parse(cleanedData);

				// Personnalisateur : announcement bar ne doit pas swiper en moins d'1s
				const isDesignMode = typeof window.Shopify !== 'undefined' && window.Shopify.designMode;
				const isAnnouncementBar = element.classList.contains('mm-announcement-bar-content');
				if (isDesignMode && isAnnouncementBar && config.autoplay) {
					config.interval = Math.max(config.interval || 4000, 3000);
				}

				// Personnalisateur : n'afficher qu'une seule pagination (MM-Select-Cards), pas footer/cart/ui-kit
				if (isDesignMode && config.pagination !== false) {
					const isSelectCards = element.classList.contains('mm-select-cards-splide');
					if (!isSelectCards) {
						config.pagination = false;
					}
				}

				splideInstance = new Splide(element, config).mount();
			} else {
				splideInstance = new Splide(element).mount();
			}
			element._splide = splideInstance;
			splideInstance.on('ready', () => { element.classList.add('mm-splide-ready'); });

			const progressBar = element.querySelector('.splide__progress__bar');
			if (progressBar && splideInstance) {
				const updateProgress = function() {
					try {
						const end = splideInstance.Components.Controller.getEnd() + 1;
						const rate = Math.min((splideInstance.index + 1) / end, 1);
						progressBar.style.width = String(100 * rate) + '%';
					} catch (err) {
						window.log('error', 'SplideManager', 'Progress bar update failed');
					}
				};
				splideInstance.on('mounted ready', updateProgress);
				splideInstance.on('move moved', updateProgress);
				splideInstance.on('scrolled', updateProgress); // free drag mode
				requestAnimationFrame(() => setTimeout(updateProgress, 50));
			}

			const sectionElement = element.closest('section');
			if (sectionElement && sectionsWithSplide) {
				const sectionName = sectionElement.className || 'unnamed-section';
				if (!sectionsWithSplide.includes(sectionName)) {
					sectionsWithSplide.push(sectionName);
				}
			}

			return 1;
		} catch (error) {
			window.log('error', 'SplideManager', `JSON parsing failed for element: ${element.className}`);
			try {
				const fallbackInstance = new Splide(element).mount();
				element._splide = fallbackInstance;
				return 1;
			} catch (fallbackError) {
				window.log('error', 'SplideManager', `Fallback initialization failed: ${fallbackError.message}`);
				return 0;
			}
		}
	},

	/**
	 * Réinitialise uniquement les Splides contenus dans une section (personnaliseur Shopify).
	 * Évite de détruire/recréer tous les Splides de la page (ex. announcement bar).
	 */
	initInSection(sectionElement) {
		if (!sectionElement || !window.Splide) return;
		const splideElements = sectionElement.querySelectorAll('.splide');
		const sectionsWithSplide = [];

		splideElements.forEach(element => {
			if (element._splide) {
				try {
					element._splide.destroy();
				} catch (e) {
					// Ignorer les erreurs de destruction
				}
			}
			SplideManager._initOne(element, sectionsWithSplide);
		});

		if (splideElements.length > 0) {
			window.emit('global:splide-initialized', {
				count: splideElements.length,
				sections: sectionsWithSplide,
				scope: 'section'
			});
		}
	},

	/**
	 * Initialize all Splide carousels with their data-splide configurations
	 */
	init(forceReinit = false) {
		const splideElements = window.getElements('.splide');
		if (!splideElements.length || !window.Splide) return;

		let initializedCount = 0;
		const sectionsWithSplide = [];

		splideElements.forEach(element => {
			if (element._splide && forceReinit) {
				try {
					element._splide.destroy();
				} catch (e) {
					// Ignorer les erreurs de destruction
				}
			}

			if (element._splide && !forceReinit) return;

			initializedCount += SplideManager._initOne(element, sectionsWithSplide);
		});

		if (initializedCount > 0) {
			window.emit('global:splide-initialized', {
				count: initializedCount,
				sections: sectionsWithSplide
			});
		}
	}
};

// Personnaliseur Shopify : réinitialiser UNIQUEMENT les Splides de la section concernée
// (évite que l'announcement bar et les autres Splides "swipent" à toute vitesse)
document.addEventListener('shopify:section:load', (event) => {
	const sectionId = event.detail?.sectionId;
	if (sectionId) {
		const sectionEl = document.getElementById('shopify-section-' + sectionId);
		if (sectionEl) {
			setTimeout(() => SplideManager.initInSection(sectionEl), 200);
			return;
		}
	}
	setTimeout(() => SplideManager.init(true), 200);
});

document.addEventListener('shopify:section:select', (event) => {
	const sectionId = event.detail?.sectionId;
	if (sectionId) {
		const sectionEl = document.getElementById('shopify-section-' + sectionId);
		if (sectionEl) {
			setTimeout(() => SplideManager.initInSection(sectionEl), 200);
			return;
		}
	}
	setTimeout(() => SplideManager.init(true), 200);
});

/*********************************************************************************************
 * ❓ FAQ MODULE
 */
const FAQManager = {
	/**
	 * Initialize FAQ tabs functionality
	 * Gestion des onglets (filtres) et affichage des groupes FAQ.
	 * Les dropdowns individuels sont gérés par le snippet mm-dropdown (DropdownManager).
	 */
	init() {
		// Pour chaque section FAQ sur la page
		const sections = window.getElements('[data-section-page-faq-id]');
		if (!sections.length) return;

		sections.forEach((section) => {
			const filters = section.querySelectorAll('.mm-faq-section-tabs-title');
			const faqContents = section.querySelectorAll('.mm-faq-dropdown');

			// Si pas de tabs (un seul groupe FAQ), rendre visible le seul groupe
			if (filters.length === 0 && faqContents.length > 0) {
				faqContents[0].classList.add('active');
			}
			// Si plusieurs tabs, activer le premier
			else if (filters.length > 0 && faqContents.length > 0) {
				filters[0].classList.add('active');
				faqContents[0].classList.add('active');
			}

			// Gestion des clics sur les tabs (seulement si tabs présents)
			filters.forEach((filter, index) => {
				filter.addEventListener('click', () => {
					filters.forEach(f => f.classList.remove('active'));
					faqContents.forEach(content => content.classList.remove('active'));

					filter.classList.add('active');
					faqContents[index].classList.add('active');

					window.emit('global:faq-tab-changed', {
						index,
						section: section.getAttribute('data-section-page-faq-id')
					});
				});
			});
		});
	}
};

/*********************************************************************************************
 * 🎯 UI COMPONENTS MANAGER
 */
const UIComponentsManager = {
	/**
	 * Initialize all UI components
	 */
	init() {
		DropdownManager.init();
		ReadMoreManager.init();
		ExternalLinksManager.init();
		SplideManager.init();
		FAQManager.init();
		DocumentHeightManager.update();
	},

	/**
	 * Handle window resize
	 */
	onResize() {
		DocumentHeightManager.update();
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.LoaderManager = LoaderManager;
window.DropdownManager = DropdownManager;  
window.ReadMoreManager = ReadMoreManager;
window.ExternalLinksManager = ExternalLinksManager;
window.SplideManager = SplideManager;
window.FAQManager = FAQManager;
window.DocumentHeightManager = DocumentHeightManager;
window.UIComponentsManager = UIComponentsManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
// Active - Used in mm_cart.js
window.mmLoadThis = (elem) => LoaderManager.loadThis(elem);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	UIComponentsManager.init();
});

// Safari fix : recalcule les dimensions de tous les Splides après que les images
// et le layout sont complètement calculés. Corrige le bug "deuxième Splide cassé
// sur Safari" causé par un timing de rendu différent de Chrome.
window.addEventListener('load', () => {
	requestAnimationFrame(() => {
		document.querySelectorAll('.splide').forEach(el => {
			if (el._splide) {
				try { el._splide.refresh(); } catch (e) {}
			}
		});
	});
});

window.addEventListener('resize', () => {
	UIComponentsManager.onResize();
});